import React from "react";
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
} from "react-hook-form";
import {
  LuAlertCircle,
  LuBoxes,
  LuPackage,
  LuRefreshCw,
  LuRuler,
  LuTag,
  LuTrash2,
} from "react-icons/lu";

import CuttingCategoryBoxes, {
  CuttingCategoryBox,
  CuttingCategoryOption,
  itemIconClass,
  lineIconClass,
  readOnlyClass,
  rowTitleClass,
} from "../cuttingLines/CuttingCategoryBoxes";
import VmcSourceChip from "./VmcSourceChip";
import MaterialPhoto from "../materialLines/MaterialPhoto";
import Input from "../ui/Input";
import RateInput from "../ui/RateInput";
import cx from "../ui/cx";
import {
  arrayRemoveButtonClass,
  arrayRowClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
} from "../ui/styles";
import { formatLength, lineName } from "../../static/cuttingLine";
import {
  formatQty,
  isWholeQty,
  sanitizeQty,
} from "../../static/materialLine";
import { hasRate } from "../../static/processRate";
import { CuttingRateSource } from "../../types/cuttingOrder";
import { IVmcOrderFormInput, VmcSource } from "../../types/vmcOrder";

// What the form shows with one line, worked out from the line, the party's options and, on an order being edited, the
// line as saved. Like a turning order line, with the kind of pieces it takes (cut or turned).
export type VmcLineView = {
  key: string;
  // The kind of pieces the line takes, shown as a chip in its title.
  source: VmcSource;
  // The item's category (0 without one): the line sits in that category's box.
  categoryId: number;
  code: string;
  sizeName: string | null;
  imageUrl: string | null;
  materialName: string | null;
  cutLength: number | null;
  // Only for a user who may see rates: the rate typed on the line, where it came from (3 when typed over the one the
  // line would copy), and what to show under the rate box.
  rate?: number;
  rateSource?: CuttingRateSource;
  rateHint: string | null;
  received: number;
  closed: boolean;
  // The pieces of its kind ready for the line (null until the party's details load), and whether its qty is above what
  // it may take.
  ready: number | null;
  over: boolean;
  // Why the line can't be saved as it is, if it can't.
  problem: string | null;
  // How the item and party pages differ from what a saved line copied, which "નવું લાગુ કરો" would copy again.
  changes: string[];
};

const lockedRateTitle = "મળી ગયેલ આઈટમ નો ભાવ બદલી શકાશે નહીં";

const isLocked = (view: VmcLineView) => view.received > 0 || view.closed;

type RowProps = {
  control: Control<IVmcOrderFormInput>;
  errors: FieldErrors<IVmcOrderFormInput>;
  index: number;
  // Its place in its box, from 1.
  number: number;
  view: VmcLineView;
  refreshed: boolean;
  canPick: boolean;
  pickerLabel: string;
  withRates: boolean;
  sizeUnit: string;
  onRemove: (index: number) => void;
  onOpenPicker: () => void;
  onRefresh: (index: number, refresh: boolean) => void;
};

// One line, like a cutting order row: its photo, the item (a button that opens its category's picker), the cut length or
// for a user who may see rates the rate, the qty with the pieces ready under it, and what has changed on the item
// page since it was saved.
const LineRow = ({
  control,
  errors,
  index,
  number,
  view,
  refreshed,
  canPick,
  pickerLabel,
  withRates,
  sizeUnit,
  onRemove,
  onOpenPicker,
  onRefresh,
}: RowProps) => {
  const qtyError = errors?.items?.[index]?.qty;
  const rateError = errors?.items?.[index]?.rate;
  const locked = isLocked(view);
  const name = lineName(view.code, view.sizeName);

  const removeButton = !locked && (
    <button
      type="button"
      title="કાઢી નાખો"
      className={arrayRemoveButtonClass}
      onClick={() => onRemove(index)}
    >
      <LuTrash2 className="h-5 w-5" />
    </button>
  );

  return (
    <div className={arrayRowClass}>
      <div className={rowTitleClass}>
        <span className="flex flex-wrap items-center gap-x-2">
          આઈટમ - {number}
          <VmcSourceChip source={view.source} />
        </span>
        <span className="md:hidden">{removeButton}</span>
      </div>

      {/* The item's photo comes first, so the line is known at a glance. */}
      <div className="md:col-span-1 md:self-center">
        <MaterialPhoto
          name={view.code}
          imageUrl={view.imageUrl}
          className="h-20 w-20 rounded-md border border-gray-200 md:aspect-square md:h-auto md:w-full"
          compact
        />
      </div>

      <div className="space-y-2 md:col-span-6">
        <label className={fieldLabelClass}>આઈટમ</label>
        <button
          type="button"
          disabled={!canPick}
          title={canPick ? "આઈટમ બદલો" : pickerLabel}
          onClick={onOpenPicker}
          className={cx(
            fieldInputClass(!!view.problem),
            "relative flex w-full items-center bg-white text-left disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
          )}
        >
          <LuPackage className={itemIconClass} />
          <span className="truncate">{name}</span>
        </button>
        <p className="truncate text-xs text-gray-500">
          મટીરીયલ: {view.materialName || "-"}
          {withRates &&
            ` • લંબાઈ: ${
              view.cutLength !== null
                ? formatLength(view.cutLength, sizeUnit)
                : "-"
            }`}
        </p>
      </div>

      {withRates ? (
        <div className="space-y-2 md:col-span-2">
          <label
            htmlFor={`vmc-line-rate-${index}`}
            className={fieldLabelClass}
          >
            ભાવ <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name={`items.${index}.rate`}
            rules={{ validate: (value) => hasRate(value) }}
            render={({ field: rateField }) => (
              <RateInput
                ref={rateField.ref}
                id={`vmc-line-rate-${index}`}
                name={rateField.name}
                value={rateField.value}
                placeholder="0.00"
                disabled={locked}
                title={locked ? lockedRateTitle : undefined}
                hasError={!!rateError}
                onChange={rateField.onChange}
                onBlur={rateField.onBlur}
                icon={LuTag}
                iconClassName={lineIconClass}
                className="disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            )}
          />
          {rateError ? (
            <p className={fieldErrorTextClass}>
              <LuAlertCircle className="h-4 w-4 mr-1" />
              ભાવ દાખલ કરો (0 પણ ચાલે)
            </p>
          ) : (
            view.rateHint && (
              <p className="truncate text-xs text-gray-500">{view.rateHint}</p>
            )
          )}
        </div>
      ) : (
        <div className="space-y-2 md:col-span-2">
          <label className={fieldLabelClass}>કટીંગ લંબાઈ</label>
          <div className="relative">
            <LuRuler className={lineIconClass} />
            <div className={readOnlyClass}>
              {view.cutLength !== null
                ? formatLength(view.cutLength, sizeUnit)
                : "-"}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 md:col-span-2">
        <label htmlFor={`vmc-line-qty-${index}`} className={fieldLabelClass}>
          જથ્થો <span className="text-red-500">*</span>
        </label>
        <Controller
          control={control}
          name={`items.${index}.qty`}
          rules={{ validate: (value) => isWholeQty(value) }}
          render={({ field: qtyField }) => (
            <div className="relative">
              <LuBoxes className={lineIconClass} />
              <Input
                id={`vmc-line-qty-${index}`}
                ref={qtyField.ref}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="નંગ"
                value={qtyField.value}
                onChange={(event) =>
                  qtyField.onChange(sanitizeQty(event.target.value))
                }
                onBlur={qtyField.onBlur}
                className={cx(fieldInputClass(!!qtyError), "bg-white")}
              />
            </div>
          )}
        />
        {qtyError ? (
          <p className={fieldErrorTextClass}>
            <LuAlertCircle className="h-4 w-4 mr-1" />
            આખો જથ્થો દાખલ કરો
          </p>
        ) : (
          <>
            {view.ready !== null && (
              <p
                className={cx(
                  "text-xs",
                  view.over ? "text-red-600" : "text-gray-500"
                )}
              >
                તૈયાર: {formatQty(view.ready)}
              </p>
            )}
            {locked && (
              <p className="text-xs text-gray-500">
                મળેલ: {formatQty(view.received)}
                {view.closed && " • બંધ"}
              </p>
            )}
          </>
        )}
      </div>

      <div className="hidden md:col-span-1 md:flex md:items-start md:justify-center md:pt-7">
        {removeButton}
      </div>

      {view.problem && (
        <p className={cx(fieldErrorTextClass, "md:col-span-12 mt-0")}>
          <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
          {view.problem}
        </p>
      )}

      {view.changes.length > 0 && (
        <div className="md:col-span-12 flex flex-wrap items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
          <LuAlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="min-w-0 flex-1">
            આઈટમ માં બદલાયેલ: {view.changes.join(", ")}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-white px-2 py-1 text-xs font-semibold text-orange-800 hover:bg-orange-100"
            onClick={() => onRefresh(index, true)}
          >
            <LuRefreshCw className="h-3.5 w-3.5" />
            નવું લાગુ કરો
          </button>
        </div>
      )}

      {refreshed && !view.problem && (
        <div className="md:col-span-12 flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          <LuRefreshCw className="h-4 w-4 flex-shrink-0" />
          <span className="min-w-0 flex-1">
            સેવ કરતાં આઈટમ ની હાલની લંબાઈ અને ભાવ લાગુ થશે
          </span>
          <button
            type="button"
            className="rounded-md border border-blue-300 bg-white px-2 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100"
            onClick={() => onRefresh(index, false)}
          >
            રદ કરો
          </button>
        </div>
      )}
    </div>
  );
};

type Props = {
  control: Control<IVmcOrderFormInput>;
  fields: FieldArrayWithId<IVmcOrderFormInput, "items", "id">[];
  errors: FieldErrors<IVmcOrderFormInput>;
  views: VmcLineView[];
  boxes: CuttingCategoryBox[];
  // The categories the party has something to machine in, and every category's name for the box titles.
  categoryOptions: CuttingCategoryOption[];
  categoryLabel: (categoryId: number | null) => string | null;
  // What the category select says when it has nothing to offer.
  categoriesMessage: string;
  // Whether to show why a box can't be saved (after the first save).
  showBoxErrors: boolean;
  onAddBox: () => void;
  onRemoveBox: (box: CuttingCategoryBox) => void;
  onChangeBoxCategory: (key: string, categoryId: number | null) => void;
  onRemove: (index: number) => void;
  onOpenPicker: (categoryId: number) => void;
  onRefresh: (index: number, refresh: boolean) => void;
  refreshed: boolean[];
  canPick: boolean;
  pickerLabel: string;
  withRates: boolean;
  sizeUnit: string;
};

// The order's lines in the cutting forms' category boxes. Each row's qty (and, for a user who may see rates, its rate)
// can be changed here. A row shows the item's cut length: in a box of its own, or for a user who may see rates under
// the item, as the rate box takes its place. A received line keeps its rate, and its box.
const VmcOrderLines = ({
  control,
  fields,
  errors,
  views,
  boxes,
  categoryOptions,
  categoryLabel,
  categoriesMessage,
  showBoxErrors,
  onAddBox,
  onRemoveBox,
  onChangeBoxCategory,
  onRemove,
  onOpenPicker,
  onRefresh,
  refreshed,
  canPick,
  pickerLabel,
  withRates,
  sizeUnit,
}: Props) => (
  <CuttingCategoryBoxes
    boxes={boxes}
    categoryOptions={categoryOptions}
    categoryLabel={categoryLabel}
    categoriesMessage={categoriesMessage}
    showBoxErrors={showBoxErrors}
    isLocked={(index) => !!views[index] && isLocked(views[index])}
    canPick={canPick}
    pickerLabel={pickerLabel}
    onAddBox={onAddBox}
    onRemoveBox={onRemoveBox}
    onChangeBoxCategory={onChangeBoxCategory}
    onOpenPicker={onOpenPicker}
    renderRow={(index, number, openPicker) => {
      const field = fields[index];
      const view = views[index];

      return (
        field &&
        view && (
          <LineRow
            key={field.id}
            control={control}
            errors={errors}
            index={index}
            number={number}
            view={view}
            refreshed={!!refreshed[index]}
            canPick={canPick}
            pickerLabel={pickerLabel}
            withRates={withRates}
            sizeUnit={sizeUnit}
            onRemove={onRemove}
            onOpenPicker={openPicker}
            onRefresh={onRefresh}
          />
        )
      );
    }}
  />
);

export default VmcOrderLines;
